/**
 * OrderMetadataContext - Contexto de Metadados do Pedido
 * 
 * Responsabilidades:
 * - Gerenciar metadados do pedido (tipo, frete, etc)
 * - Coordenar finalização do pedido (usa outros contextos)
 * - Calcular valor total final
 * - Persistir no localStorage (criptografado)
 * - Otimizado com useMemo e useCallback
 */

import React, { createContext, useState, useEffect, useMemo, useCallback, ReactNode, useContext } from 'react';
import { OrderMetadata, OrderType, FreightType, OrderStatus, FinalOrder, CheckoutCustomer, CheckoutAddress, OrderCompleteMetadata, OrderMetadataProviderProps, OrderMetadataContextType } from './orderMetaData.type';
import { saveEncrypted, loadEncrypted } from '../../utils/encryptedStorage';

import useAuth from '../../hooks/useAuth';
import { useAppConfig } from '../../hooks/useAppConfig';
import { usePluginspace } from '../../hooks/usePluginspace';
import { cepNoMask, cpfCnpjNoMask, datetimeToDateUS, telephoneNoMask } from '../../functions/mask';
import { getPlugintypeIdByCode } from '../../functions/plugintype';
import { floatToCents } from '../../functions/price_functions';
import type { OrderToSendType, CustomerType as LegacyCustomerType, OrderAddress as LegacyOrderAddress } from '../OrderContext';
import useCart from '../cartContext/cartContext';
import { useCustomer } from '../customerContext/customerContext';
import { useDiscount } from '../../hooks/useDiscount';
import { usePayment } from '../paymentContext/paymentContext';
import { useSellerInfo } from '../sellerInfoContext';

const STORAGE_KEY = 'checkout_metadata';
const STORAGE_KEY_COMPLETE = 'checkout_metadata_complete';
const PLUGINSPACE_STORAGE_KEY = 'pluginspace_context_guard';
const PLUGINSPACE_CHANGE_EVENT = 'pluginspace:change';

const DEFAULT_METADATA: OrderMetadata = {
  orderType: 'withdraw',
  freightType: 'calculated',
  freightCost: 0,
};


// ============ CONTEXT ============

export const OrderMetadataContext = createContext<OrderMetadataContextType | undefined>(undefined);

// ============ PROVIDER ============

export function OrderMetadataProvider({ children }: OrderMetadataProviderProps) {
  const { currentPluginspaceId } = useAuth();
  const { getAppConfig, getPluginconfig } = useAppConfig();
  const { getThisPluginspace } = usePluginspace();
  const [metadata, setMetadata] = useState<OrderMetadata>(() => {
    // Carregar do localStorage na inicialização
    const saved = loadEncrypted<OrderMetadata>(STORAGE_KEY);
    return saved || DEFAULT_METADATA;
  });
  const [metadataComplete, setMetadataComplete] = useState<OrderCompleteMetadata>(() => {
    const saved = loadEncrypted<OrderCompleteMetadata>(STORAGE_KEY_COMPLETE);
    return saved || { orderId: '', orderIdExt: '', receipt: '' };
  });

  // Contextos necessários para finalização
  const cart = useCart();
  const customer = useCustomer();
  const payment = usePayment();
  const discount = useDiscount();
  const sellerInfo = useSellerInfo()

  useEffect(() => {
    if (!currentPluginspaceId) return;
    const stored = loadEncrypted<{ pluginspaceId: number }>(PLUGINSPACE_STORAGE_KEY);
    const storedPluginspaceId = stored?.pluginspaceId ? String(stored.pluginspaceId) : null;
    const currentId = String(currentPluginspaceId);

    if (storedPluginspaceId && storedPluginspaceId !== currentId) {
      setMetadata(DEFAULT_METADATA);
    }

    if (storedPluginspaceId !== currentId) {
      saveEncrypted(PLUGINSPACE_STORAGE_KEY, { pluginspaceId: currentPluginspaceId });
    }
  }, [currentPluginspaceId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handlePluginspaceChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ pluginspaceId: number }>;
      const newPluginspaceId = customEvent.detail?.pluginspaceId;
      if (!newPluginspaceId) return;

      const stored = loadEncrypted<{ pluginspaceId: number }>(PLUGINSPACE_STORAGE_KEY);
      const storedPluginspaceId = stored?.pluginspaceId ? String(stored.pluginspaceId) : null;
      const nextId = String(newPluginspaceId);

      if (storedPluginspaceId && storedPluginspaceId !== nextId) {
        setMetadata(DEFAULT_METADATA);
      }

      if (storedPluginspaceId !== nextId) {
        saveEncrypted(PLUGINSPACE_STORAGE_KEY, { pluginspaceId: newPluginspaceId });
      }
    };

    window.addEventListener(PLUGINSPACE_CHANGE_EVENT, handlePluginspaceChange as EventListener);
    return () => window.removeEventListener(PLUGINSPACE_CHANGE_EVENT, handlePluginspaceChange as EventListener);
  }, []);

  // ============ PERSISTÊNCIA ============

  useEffect(() => {
    // Salvar no localStorage sempre que o estado mudar
    saveEncrypted(STORAGE_KEY, metadata);
  }, [metadata]);
  useEffect(() => {
    // Salvar no localStorage sempre que o estado mudar
    saveEncrypted(STORAGE_KEY_COMPLETE, metadataComplete);
  }, [metadataComplete]);
  // ============ AÇÕES (MEMOIZADAS) ============

  const setOrderType = useCallback((type: OrderType) => {
    setMetadata(prev => ({ ...prev, orderType: type }));
  }, []);

  const setFreightType = useCallback((type: FreightType) => {
    setMetadata(prev => ({ ...prev, freightType: type }));
  }, []);

  const setFreightCost = useCallback((cost: number) => {
    setMetadata(prev => ({ ...prev, freightCost: cost }));
    cart.setFreightCost(cost);
  }, [cart]);

  const setDeliveryAddress = useCallback((address: any) => {
    setMetadata(prev => ({ ...prev, deliveryAddress: address }));
  }, []);

  const setLocationId = useCallback((id: string) => {
    setMetadata(prev => ({ ...prev, locationId: id }));
  }, []);

  const setTableNumber = useCallback((table: string) => {
    setMetadata(prev => ({ ...prev, tableNumber: table }));
  }, []);

  const setNotes = useCallback((notes: string) => {
    setMetadata(prev => ({ ...prev, notes }));
  }, []);

  const setOrderReference = useCallback((reference: string) => {
    setMetadata(prev => ({ ...prev, orderReference: reference }));
  }, []);

  const setOrderStatus = useCallback((status: OrderStatus) => {
    setMetadata(prev => ({ ...prev, status }));
  }, []);

  const clearMetadata = useCallback(() => {
    setMetadata(DEFAULT_METADATA);
  }, []);

  const setMetaDataComplete = useCallback((metadataComplete: OrderCompleteMetadata) => {
    setMetadataComplete(metadataComplete);
  }, []);

  // ============ QUERIES (MEMOIZADAS) ============

  const calculateTotal = useCallback(() => {
    const subtotal = cart.subtotalWithDiscount;
    const freightCost = metadata.freightCost;
 
    // Adicionar frete (desconto de frete já está em discountValue)
    const total = subtotal + freightCost;

    return Math.max(0, total);
  }, [cart.subtotal, metadata.freightCost, discount.discounts]);

  const isOrderComplete = useCallback(() => {
    // Validar carrinho
    if (cart.cartCount === 0) return false;

    // Validar cliente
    if (!customer.isCustomerComplete()) return false;

    // Validar endereço (se delivery)
    if (metadata.orderType === 'delivery' && !customer.isAddressComplete()) return false;

    // Validar pagamento
    if (!payment.isPaymentComplete()) return false;

    return true;
  }, [
    cart.cartCount,
    customer.isCustomerComplete,
    customer.isAddressComplete,
    payment.isPaymentComplete,
    metadata.orderType,
  ]);

  // ============ AÇÃO FINAL - COORDENA TODOS OS CONTEXTOS ============

  const finalizeOrder = useCallback(async (): Promise<FinalOrder> => {
    if (!isOrderComplete()) {
      throw new Error('Pedido incompleto. Verifique todos os dados.');
    }

    // Montar pedido final
    const finalOrder: FinalOrder = {
      cart: {
        products: cart.products,
        subtotal: cart.subtotal,
      },
      customer: customer.customer!,
      payment: {
        method: payment.paymentMethod!,
        card: payment.card || undefined,
        installments: payment.selectedInstallment?.number,
      },
      discount: undefined,
      metadata: {
        ...metadata,
        orderId: metadata.orderId || `ORD-${Date.now()}`,
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      total: calculateTotal(),
    };

    // Atualizar metadata com ID e status
    setMetadata(prev => ({
      ...prev,
      orderId: finalOrder.metadata.orderId,
      status: finalOrder.metadata.status,
      createdAt: finalOrder.metadata.createdAt,
      updatedAt: finalOrder.metadata.updatedAt,
    }));

    return finalOrder;
  }, [
    isOrderComplete,
    cart.products,
    cart.subtotal,
    customer.customer,
    payment.paymentMethod,
    payment.card,
    payment.selectedInstallment,
    discount.discounts,
    metadata,
    calculateTotal,
  ]);

  const generateDataToSendOrderCard = useCallback(async (
    customerToSend: LegacyCustomerType | CheckoutCustomer,
    encryptedCard: string,
    uuid: string,
    retirarServico: boolean,
    pix?: boolean,
    offline?: boolean,
    cardNumber?: number
  ): Promise<OrderToSendType | undefined> => {
    const pluginspaceData = await getThisPluginspace();
    const config = await getAppConfig();

    const baseCustomer = customerToSend || customer.customer;
    if (!pluginspaceData || !baseCustomer) return undefined;



    //Caso nao tenha o Id do cliente, nao tem como enviar os dados para o gateway, entao retorna undefined
    //Caso nao tenha o Id do cliente, nao tem como enviar os dados para o gateway, entao retorna undefined
    //Caso nao tenha o Id do cliente, nao tem como enviar os dados para o gateway, entao retorna undefined
    if (!baseCustomer.id) return undefined

    const resolvePhone = (c: LegacyCustomerType | CheckoutCustomer) =>
      'telephone' in c ? c.telephone : c.phone;
    const resolveEmail = (c: LegacyCustomerType | CheckoutCustomer) =>
      'email' in c ? c.email || '' : '';
    const resolveCpf = (c: LegacyCustomerType | CheckoutCustomer) =>
      'cpf' in c ? c.cpf || '' : '';
    const resolveBirthdate = (c: LegacyCustomerType | CheckoutCustomer) =>
      'birthdate' in c && c.birthdate ? datetimeToDateUS(c.birthdate as any) : '';
    const resolveGender = (c: LegacyCustomerType | CheckoutCustomer) =>
      'gender' in c ? (c.gender || '') : '';

    const normalizeAddress = (
      address?: CheckoutAddress | LegacyOrderAddress | null
    ): LegacyOrderAddress | undefined => {
      if (!address) return undefined;

      const isCheckoutAddress = 'street' in address || 'neighborhood' in address;

      const street = isCheckoutAddress
        ? (address as CheckoutAddress).street
        : (address as LegacyOrderAddress).address;
      const neighborhood = isCheckoutAddress
        ? (address as CheckoutAddress).neighborhood
        : (address as LegacyOrderAddress).district;
      const cep = (address as any).cep || '';
      const city = (address as any).city || '';
      const number = (address as any).number || '';
      const state = isCheckoutAddress
        ? (address as CheckoutAddress).state
        : (address as LegacyOrderAddress).state;
      const complement = isCheckoutAddress
        ? (address as CheckoutAddress).complement
        : (address as LegacyOrderAddress).complement;
      const reference = isCheckoutAddress
        ? (address as CheckoutAddress).reference
        : (address as LegacyOrderAddress).reference;
      const name = isCheckoutAddress
        ? (address as any).name
        : (address as LegacyOrderAddress).name;
      const telephone = isCheckoutAddress
        ? (address as any).telephone
        : (address as LegacyOrderAddress).telephone;
      const ibge = isCheckoutAddress
        ? (address as any).ibge
        : (address as LegacyOrderAddress).ibge;
      const extra = isCheckoutAddress
        ? (address as any).extra
        : (address as LegacyOrderAddress).extra;

      return {
        address: street || '',
        cep: cep || '',
        district: neighborhood || '',
        city: city || '',
        number: number || '',
        state: state || '',
        complement: complement || '',
        reference: reference || metadata.notes || '',
        name: name || ('name' in baseCustomer ? baseCustomer.name : ''),
        telephone: telephone || resolvePhone(baseCustomer) || '',
        ibge: ibge || '',
        extra: extra || '',
      };
    };

    const addressSource: CheckoutAddress | LegacyOrderAddress | null | undefined =
      (metadata.deliveryAddress as CheckoutAddress | LegacyOrderAddress | undefined) ||
      (customer.customerAddress as CheckoutAddress | LegacyOrderAddress | undefined) ||
      null;

    const orderAddress: LegacyOrderAddress | undefined = normalizeAddress(addressSource);

    let totalAmountDiscount = 0;
    const products = cart.products.map((product) => {
      const discountValue =
        product.unitPrice === product.promotionalPrice || product.promotionalPrice === 0
          ? 0
          : floatToCents(product.unitPrice) - floatToCents(product.promotionalPrice);
      totalAmountDiscount += discountValue * product.quantity;

      return {
        hash: window.btoa(product.code),
        id: Number(product.id) || 0,
        productName: product.name,
        code: product.code,
        quantity: product.quantity,
        discount: discountValue,
        price:
          product.unitPrice === product.promotionalPrice || product.promotionalPrice === 0
            ? floatToCents(product.unitPrice)
            : floatToCents(product.promotionalPrice),
        unitPrice: floatToCents(product.unitPrice),
        codFilial: String(pluginspaceData.id),
        info: '',
        chair: '',
        notes: '',
        productSku: product.sku,
        variation: product.variation as any,
        additionals: undefined,
        fractions: undefined,
        type: undefined,
        typeFractions: undefined,
        observation: product.observation,
        orderId: undefined,
        image: product.image || '',
        measures: product.measures ? product.measures : null,
      };
    });

    const orderDate = new Date().toLocaleString('sv');
    const typeId = getPlugintypeIdByCode(pluginspaceData.type);
    const discountGeneralValue = discount.discounts.find((d) => d.type === "coupon")?.value || 0;
    const discountPaymentValue = discount.discounts.find((d) => d.type === "payment")?.value || 0;
    totalAmountDiscount += floatToCents(discountGeneralValue ) + floatToCents(discountPaymentValue);

    const paymentMethod = payment.paymentMethod;
    const gatewayId = payment.gatewayId
    const installment = payment.selectedInstallment;

    const subscriptionStorage = localStorage.getItem(window.btoa('subscription_notification'));
    let subscription = null;
    if (
      subscriptionStorage &&
      window.atob(subscriptionStorage) &&
      JSON.parse(window.atob(subscriptionStorage))
    ) {
      subscription = JSON.parse(window.atob(subscriptionStorage));
    }

    const dataGateway: OrderToSendType = {
      orderDate: orderDate,
      totalAmountDiscount: totalAmountDiscount,
      sellerCpfCnpj: sellerInfo.data.seller.cpf,
      sellerCode: sellerInfo.data.seller.sellerCode,
      guidCart: null,
      customerId: Number(baseCustomer.id) || 0,
      pluginspaceId: pluginspaceData.id,
      pluginconfigId:
        config && config.content.pdv.pluginconfigId
          ? config.content.pdv.pluginconfigId
          : 0,
      plugintypeId: typeId ? typeId : 0,
      webappHash: pluginspaceData.hash,
      locationId: metadata.locationId ? Number(metadata.locationId) : (cart.products[0]?.locationId || 0),
      sessionIdGuid: uuid,
      deliveryFee: floatToCents(metadata.freightCost || cart.freightCost || 0),
      discountGeneral: floatToCents(totalAmountDiscount),
      totalAmountOrder: floatToCents(calculateTotal()),
      account: '',
      gatewayId: gatewayId,
      paymentOffline: offline ? offline : false,
      changeMoney: payment.changePayment ? floatToCents(payment.changePayment) : 0,
      building: '0',
      sector: '0',
      living: '0',
      codePay: paymentMethod?.codePay || '0',
      codePlan: paymentMethod?.codePlan || 0,
      paymentItemData:
        paymentMethod?.paymentItemData
          ? {
            ...paymentMethod.paymentItemData,
            ...(paymentMethod.paymentItemData.typePay === 'Credit' ||
              paymentMethod.paymentItemData.typePay === 'Others'
              ? {
                installment: installment ? installment.number : 1,
                installmentValue: installment ? parseFloat(installment.value.toFixed(2)) : 0,
              }
              : {}),
          }
          : null,
      nameGateway: paymentMethod?.nameGateway || '',
      cpfOrder: resolveCpf(baseCustomer) ? cpfCnpjNoMask(resolveCpf(baseCustomer)) : '',
      typeModality: metadata.orderType || 'delivery',
      orderAddress: orderAddress,
      customer: [
        {
          customerId: Number(baseCustomer.id) || 0,
          name: baseCustomer.name,
          phone: telephoneNoMask(resolvePhone(baseCustomer) || ''),
          email: resolveEmail(baseCustomer),
          zipcode: orderAddress?.cep ? cepNoMask(orderAddress.cep) : '',
          cpf:
            cpfCnpjNoMask(resolveCpf(baseCustomer)).length > 11
              ? ''
              : cpfCnpjNoMask(resolveCpf(baseCustomer)),
          cnpj:
            cpfCnpjNoMask(resolveCpf(baseCustomer)).length > 11
              ? cpfCnpjNoMask(resolveCpf(baseCustomer))
              : '',
          optinWhatsapp:
            'wppNotification' in baseCustomer ? !!baseCustomer.wppNotification : false,
          lgpdDataAceiteCarta:
            'acceptTermDate' in baseCustomer && baseCustomer.acceptTermDate
              ? String(baseCustomer.acceptTermDate)
              : '',
          birthDate: resolveBirthdate(baseCustomer),
          gender: resolveGender(baseCustomer),
          doc: cpfCnpjNoMask(resolveCpf(baseCustomer)).length > 11 ? 'cnpj' : 'cpf',
          inscEstadual:
            'inscEstadual' in baseCustomer &&
              baseCustomer.inscEstadual &&
              baseCustomer.inscEstadual !== '' &&
              cpfCnpjNoMask(resolveCpf(baseCustomer)).length > 11
              ? baseCustomer.inscEstadual
              : null,
          idTerms: 0,
          customerAddress: [
            {
              zipcode: orderAddress?.cep ? cepNoMask(orderAddress.cep) : '',
              street: orderAddress?.address || '',
              number: orderAddress?.number || '',
              district: orderAddress?.district || '',
              city: orderAddress?.city || '',
              state: orderAddress?.state || '',
              extra: orderAddress?.complement || '',
              ibge: orderAddress?.ibge || '',
            },
          ],
        },
      ],
      removeService: retirarServico,
      observation: metadata.notes || undefined,
      products: products,
      gatewayCard: {
        encryptedCard: encryptedCard,
      },
      subscription,
      tablePayment: '',
      serviceTaxes: 0,
      cupomDiscount: discount.discounts.find((d) => d.type === "coupon")?.code || '',
      typePayment: paymentMethod?.typePayment || undefined,
    };

    const cashbackRedeemAmount = Number(
      discount.discounts.find((d) => d.type === "cashback")?.value || 0
    );
    if (cashbackRedeemAmount > 0) {
      dataGateway.cashbackRedeemAmount = Number(cashbackRedeemAmount.toFixed(2));
    }

    if (pix) {
      dataGateway.pix = true;
    }

    if (discount.discounts.find((d) => d.type === "payment")) {
      dataGateway.discountPaymentType = {
        type: discount.discounts.find((d) => d.type === "payment")?.type ?? '',
        discountValue: String(discount.discounts.find((d) => d.type === "payment")?.value || 0),
      };
    }

    return dataGateway;
  }, [
    cart.products,
    cart.freightCost,
    calculateTotal,
    customer.customer,
    customer.customerAddress,
    discount.discounts,
    getAppConfig,
    getPluginconfig,
    getThisPluginspace,
    metadata.deliveryAddress,
    metadata.freightCost,
    metadata.locationId,
    metadata.notes,
    metadata.orderType,
    payment.paymentMethod,
    payment.selectedInstallment,
  ]);

  // ============ VALUE (MEMOIZADO) ============

  const value = useMemo<OrderMetadataContextType>(() => ({
    // Estado
    metadata,
    metadataComplete,

    // Ações
    setOrderType,
    setFreightType,
    setFreightCost,
    setDeliveryAddress,
    setLocationId,
    setTableNumber,
    setNotes,
    setOrderReference,
    setOrderStatus,
    clearMetadata,
    setMetaDataComplete,

    // Queries
    calculateTotal,
    isOrderComplete,

    // Ação final
    finalizeOrder,
    generateDataToSendOrderCard,
  }), [
    metadata,
    metadataComplete,
    setOrderType,
    setFreightType,
    setFreightCost,
    setDeliveryAddress,
    setLocationId,
    setTableNumber,
    setNotes,
    setOrderReference,
    setOrderStatus,
    clearMetadata,
    calculateTotal,
    isOrderComplete,
    finalizeOrder,
    generateDataToSendOrderCard,
    setMetaDataComplete,
  ]);

  return <OrderMetadataContext.Provider value={value}>{children}</OrderMetadataContext.Provider>;
}

/**
 * Hook principal - retorna todo o contexto
 */
export function useOrderMetaData() {
  const context = useContext(OrderMetadataContext);
  if (!context) {
    throw new Error('useOrderMetadata must be used within OrderMetadataProvider');
  }
  return context;
}

/**
 * Hook seletor - apenas metadados
 */
export function useOrderData() {
  const { metadata } = useOrderMetaData();
  return useMemo(() => metadata, [metadata]);
}

/**
 * Hook seletor - apenas tipo de pedido
 */
export function useOrderType() {
  const { metadata } = useOrderMetaData();
  return useMemo(() => metadata.orderType, [metadata.orderType]);
}

/**
 * Hook seletor - apenas tipo de frete
 */
export function useFreightType() {
  const { metadata } = useOrderMetaData();
  return useMemo(() => metadata.freightType, [metadata.freightType]);
}

/**
 * Hook seletor - apenas custo de frete
 */
export function useFreightCost() {
  const { metadata } = useOrderMetaData();
  return useMemo(() => metadata.freightCost, [metadata.freightCost]);
}

/**
 * Hook seletor - apenas total do pedido
 */
export function useOrderTotal() {
  const { calculateTotal } = useOrderMetaData();
  return useMemo(() => calculateTotal(), [calculateTotal]);
}

/**
 * Hook seletor - apenas ações
 * NUNCA re-renderiza
 */
export function useOrderActions() {
  const {
    setOrderType,
    setFreightType,
    setFreightCost,
    setDeliveryAddress,
    setLocationId,
    setTableNumber,
    setNotes,
    setOrderReference,
    setOrderStatus,
    clearMetadata,
    finalizeOrder,
    generateDataToSendOrderCard,
    setMetaDataComplete,
  } = useOrderMetaData();

  return useMemo(() => ({
    setOrderType,
    setFreightType,
    setFreightCost,
    setDeliveryAddress,
    setLocationId,
    setTableNumber,
    setNotes,
    setOrderReference,
    setOrderStatus,
    clearMetadata,
    finalizeOrder,
    generateDataToSendOrderCard,
    setMetaDataComplete,
  }), [
    setOrderType,
    setFreightType,
    setFreightCost,
    setDeliveryAddress,
    setLocationId,
    setTableNumber,
    setNotes,
    setOrderReference,
    setOrderStatus,
    clearMetadata,
    finalizeOrder,
    generateDataToSendOrderCard,
    setMetaDataComplete,
  ]);
}

/**
 * Hook seletor - validação do pedido
 */
export function useOrderValidation() {
  const { isOrderComplete } = useOrderMetaData();
  return useMemo(() => isOrderComplete(), [isOrderComplete]);
}
